
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ColumnBlockConfiguration } from "@/types/block-configurations";
import {
  BackgroundControls,
  PaddingControls,
  BorderControls,
  ResponsiveControls,
} from "../shared";

interface ColumnBlockPropertiesProps {
  config: ColumnBlockConfiguration;
  onChange: (config: ColumnBlockConfiguration) => void;
}

export function ColumnBlockProperties({ config, onChange }: ColumnBlockPropertiesProps) {
  const updateField = <K extends keyof ColumnBlockConfiguration>(
    field: K,
    value: ColumnBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Width Presets */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Column Width</Label>
        <div className="grid grid-cols-3 gap-2 mb-1">
          {['25%', '33%', '50%', '67%', '75%', '100%'].map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => updateField('width', preset)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                config.width === preset
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-input hover:bg-accent'
              }`}
            >
              {preset}
            </button>
          ))}
        </div>
        <Input
          type="text"
          value={config.width}
          onChange={(e) => updateField('width', e.target.value)}
          placeholder="50% or 300px or auto"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Format: 50%, 300px, or auto
        </p>
      </div>

      {/* Vertical Align */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Vertical Align</Label>
        <Select
          value={config.verticalAlign}
          onChange={(e) => updateField('verticalAlign', e.target.value as 'top' | 'middle' | 'bottom')}
        >
          <option value="top">Top</option>
          <option value="middle">Middle</option>
          <option value="bottom">Bottom</option>
        </Select>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" className="w-full">
        
        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent>
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="border">
          <AccordionTrigger>Border</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive & Stacking</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <ResponsiveControls
                value={config.responsive}
                onChange={(responsive) => updateField('responsive', responsive)}
              />
              <div className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  id="mobile-stack"
                  checked={config.mobileStack}
                  onChange={(e) => updateField('mobileStack', e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-input"
                />
                <Label htmlFor="mobile-stack" className="text-xs cursor-pointer">
                  Stack on mobile
                </Label>
              </div>
              {config.mobileStack && (
                <div>
                  <Label className="text-xs font-medium mb-1 block">Stack Order</Label>
                  <Input
                    type="number"
                    value={config.mobileStackOrder}
                    onChange={(e) => updateField('mobileStackOrder', parseInt(e.target.value, 10) || 0)}
                    min={0}
                    max={100}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lower values appear first when stacked on mobile
                  </p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
